import React, { useEffect } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import {
  Route, Switch, Redirect, useHistory, useLocation,
} from 'react-router-dom';
import Helmet from 'react-helmet';
import { useTheme } from 'emotion-theming';
import styled from '@emotion/styled';

import Layout from '../../shared/components/Layout/Layout';
import Button from '../../shared/_TBComponents/Button';
import hexToRgba from '../../shared/utils/hexToRgba';
import AthleteNavigator from './components/AthleteNavigator';
import CalendarNavigator from './components/CalendarNavigator';
import ProgramNavigator from './components/ProgramNavigator';
import PreviewCarousel from './components/presentational/PreviewCarousel';
import useSessionState from '../calendar/components/presentational/hooks/useSessionState';
import { fetchAthletes, fetchCalendars, fetchPrograms } from './ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-top: 49px;
  background: #fff;
`;

const NavSection = styled.div`
  width: 260px;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  padding: 16px;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const MobileHomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  height: 100%;
  overflow-y: auto;
  box-sizing: border-box;
`;

const TabButtonWrapper = styled.div`
  margin-bottom: 16px;
  
  @media screen and (max-width: 992px) {
    margin-bottom: 12px;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  overflow: auto;
`;

const TAB_CONFIG = [
  {
    key: 'athletes',
    name: 'Athletes',
    subtitle: 'Workout assignments, preview, and results.',
    mobileHeight: '90px',
    desktopHeight: '110px',
  },
  {
    key: 'calendars',
    name: 'Calendars',
    subtitle: 'Scheduled workouts',
    mobileHeight: '90px',
    desktopHeight: '90px',
  },
  {
    key: 'programs',
    name: 'Programs',
    subtitle: 'Self-paced workouts',
    mobileHeight: '90px',
    desktopHeight: '90px',
  },
];

const Navigator = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const userSettings = currentUser?.userSettings;
  const manageUsers = !!userSettings?.manageUsers;
  const accountPrimaryColor = theme?.colorPrimary || currentUser?.userSkin?.primary;
  const accountPrimaryBg = hexToRgba(accountPrimaryColor, 0.05);

  const [isNarrowScreen, setIsNarrowScreen] = React.useState(
    window.matchMedia('(max-width: 992px)').matches,
  );

  const [isMobile, setIsMobile] = React.useState(
    window.matchMedia('(max-width: 767px)').matches,
  );

  // Persist last visited paths in session storage
  const [lastPath, setLastPath] = useSessionState(
    'dashboard',
    'lastPath',
    '/dashboard/athletes',
  );

  const [lastAthletesPath, setLastAthletesPath] = useSessionState(
    'dashboard',
    'lastPath:athletes',
    '/dashboard/athletes',
  );

  const [lastCalendarsPath, setLastCalendarsPath] = useSessionState(
    'dashboard',
    'lastPath:calendars',
    '/dashboard/calendars',
  );

  const [lastProgramsPath, setLastProgramsPath] = useSessionState(
    'dashboard',
    'lastPath:programs',
    '/dashboard/programs',
  );

  // Activity update for PHP
  useEffect(() => {
    if (currentUser?.id) {
      const phpAppUrl = process.env.PHP_APP_URL
        || `https://${
          process.env.ENVIRONMENT === 'PRODUCTION' ? '' : 'staging.'
        }app.teambuildr.com`;

      fetch(`${phpAppUrl}/assets/activity_update.php`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {
        // Silently fail
      });
    }
  }, [currentUser?.id]);

  // Update last visited paths
  useEffect(() => {
    if (!location.pathname.startsWith('/dashboard')) return;
    if (location.pathname === '/dashboard') return;

    const fullPath = `${location.pathname}${location.search || ''}`;
    setLastPath(fullPath);

    if (location.pathname.includes('/dashboard/calendars')) {
      setLastCalendarsPath(fullPath);
    } else if (location.pathname.includes('/dashboard/programs')) {
      setLastProgramsPath(fullPath);
    } else if (location.pathname.includes('/dashboard/athletes')) {
      setLastAthletesPath(fullPath);
    }
  }, [location.pathname, location.search]);

  // Guard against invalid paths
  useEffect(() => {
    if (lastAthletesPath && !lastAthletesPath.includes('/dashboard/athletes')) {
      setLastAthletesPath('/dashboard/athletes');
    }
    if (lastCalendarsPath && !lastCalendarsPath.includes('/dashboard/calendars')) {
      setLastCalendarsPath('/dashboard/calendars');
    }
    if (lastProgramsPath && !lastProgramsPath.includes('/dashboard/programs')) {
      setLastProgramsPath('/dashboard/programs');
    }
  }, [lastAthletesPath, lastCalendarsPath, lastProgramsPath]);

  useEffect(() => {
    if (accountCode) {
      batch(() => {
        dispatch(fetchAthletes(accountCode));
        dispatch(fetchCalendars(accountCode));
        dispatch(fetchPrograms(accountCode));
      });
    }
  }, [accountCode, dispatch]);

  // Track screen width for responsive design
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 992px)');
    const handleChange = (e) => setIsNarrowScreen(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleTabClick = (tab) => {
    if (isMobile) {
      history.push(`/dashboard/${tab}`);
      return;
    }
    if (tab === 'calendars') {
      history.push(
        lastCalendarsPath?.includes('/dashboard/calendars')
          ? lastCalendarsPath
          : '/dashboard/calendars',
      );
      return;
    }
    if (tab === 'programs') {
      history.push(
        lastProgramsPath?.includes('/dashboard/programs')
          ? lastProgramsPath
          : '/dashboard/programs',
      );
      return;
    }
    history.push(
      lastAthletesPath?.includes('/dashboard/athletes')
        ? lastAthletesPath
        : '/dashboard/athletes',
    );
  };

  const getActiveTab = () => {
    if (location.pathname.includes('/dashboard/calendars')) return 1;
    if (location.pathname.includes('/dashboard/programs')) return 2;
    return 0;
  };
  const activeTab = getActiveTab();

  const renderMobileHomeTab = ({
    key,
    name,
    subtitle,
    desktopHeight,
  }) => (
    <TabButtonWrapper key={key}>
      <Button
        name={name}
        subtitle={subtitle}
        onClick={() => handleTabClick(key)}
        large
        fullWidth
        customBorderRadius='8px'
        borderColor='#EEE'
        bgColor='ffffff'
        fontSize='18px'
        styledTextProps={{ fontWeight: 700, letterSpacing: '0.3px' }}
        styledSubtitleProps={{
          fontSize: '16px',
          color: '#000',
          fontWeight: 400,
          letterSpacing: '0.3px',
        }}
        styledWrapperProps={{
          padding: '16px 24px',
          height: desktopHeight,
          gap: '6px',
        }}
      />
    </TabButtonWrapper>
  );

  const renderTabButton = ({
    key,
    name,
    subtitle,
    mobileHeight,
    desktopHeight,
  }, index) => (
    <TabButtonWrapper key={key}>
      <Button
        name={name}
        subtitle={isNarrowScreen ? undefined : subtitle}
        onClick={() => handleTabClick(key)}
        large
        fullWidth
        customBorderRadius='8px'
        borderColor={activeTab === index && accountPrimaryColor ? accountPrimaryColor : '#EEE'}
        bgColor={activeTab === index && accountPrimaryColor ? accountPrimaryBg : 'ffffff'}
        fontSize='18px'
        styledTextProps={{ fontWeight: 700, letterSpacing: '0.3px' }}
        styledSubtitleProps={{
          fontSize: '16px',
          color: '#000',
          fontWeight: 400,
          letterSpacing: '0.3px',
        }}
        styledWrapperProps={{
          padding: isNarrowScreen ? '12px' : '16px 24px',
          height: isNarrowScreen ? mobileHeight : desktopHeight,
          borderWidth: activeTab === index && accountPrimaryColor ? '2px' : '1px',
          gap: '6px',
        }}
      />
    </TabButtonWrapper>
  );

  return (
    <Layout fullWidth>
      <Helmet>
        <title>Home | TeamBuildr</title>
      </Helmet>
      <Container>
        <NavSection>
          {TAB_CONFIG.map(renderTabButton)}
        </NavSection>
        <ContentSection>
          <Switch>
            <Route exact path='/dashboard'>
              {isMobile ? (
                <MobileHomeContainer>
                  {TAB_CONFIG.map(renderMobileHomeTab)}
                </MobileHomeContainer>
              ) : (
                <Redirect to={lastPath || '/dashboard/athletes'} />
              )}
            </Route>
            <Route path='/dashboard/athletes/:athleteId?'>
              {({ match }) => (
                <AthleteNavigator
                  athleteId={match?.params?.athleteId}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route path='/dashboard/calendars/:calendarId?'>
              {({ match }) => (
                <CalendarNavigator
                  accountCode={accountCode}
                  calendarId={match?.params?.calendarId}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
            <Route path='/dashboard/programs/:programId?'>
              {({ match }) => (
                <ProgramNavigator
                  accountCode={accountCode}
                  programId={match?.params?.programId}
                  manageUsers={manageUsers}
                  isMobile={isMobile}
                />
              )}
            </Route>
          </Switch>
        </ContentSection>
        <PreviewCarousel />
      </Container>
    </Layout>
  );
};

export default Navigator;
